import axiosInstance from '@/api/axiosInstance';
import { buildQueryString } from '@/utils/query';
import { ExportFormat } from '@/constants/export-dropdown.constants';
import {
  Tenant, TenantCreate, TenantUpdate, TenantFilterParams,
  PaginatedTenantsResponse, TenantUsageItem, TenantBranding, TenantBrandingUpdate, ExploreSettings
} from '@/types/tenant.types';
import { TENANT_ENDPOINTS } from '@/api/endpoints/tenant.endpoints';
import { FormApiService, ManagementApiService } from '@/types/management.types';

import { tr } from '@/i18n';
export const TenantManagementApiService: ManagementApiService<Tenant, TenantFilterParams> = {
  getAll: async (offset = 0, limit = 20, filters?: TenantFilterParams): Promise<PaginatedTenantsResponse> => {
    const query = buildQueryString({ ...filters, offset, limit });
    return (await axiosInstance.get<PaginatedTenantsResponse>(`${TENANT_ENDPOINTS.TENANTS}${query}`)).data;
  },
  deleteItem: async (_id: number): Promise<void> => {
    throw new Error(tr("ui.tenant_deletion_is_not_supported_use_edit_to_sus_96617e", "Tenant deletion is not supported. Use Edit to suspend the tenant instead."));
  },
  exportItems: async (format: ExportFormat, filters?: Omit<TenantFilterParams, 'offset' | 'limit'>): Promise<Blob> => {
    const query = buildQueryString({ ...filters, format: format.toLowerCase() });
    return (await axiosInstance.get<Blob>(`${TENANT_ENDPOINTS.TENANTS_EXPORT}${query}`, { responseType: 'blob' })).data;
  },
};

export const TenantFormApiService: FormApiService<Tenant, TenantCreate, TenantUpdate> = {
  createItem: async (data: TenantCreate): Promise<Tenant> => {
    const res = await axiosInstance.post<Tenant>(TENANT_ENDPOINTS.TENANTS, data);
    console.log('TenantFormApiService.createItem returned:', res.data);
    return res.data;
  },
  updateItem: async (id: number, data: TenantUpdate): Promise<Tenant> => {
    // Raw response too: AddEditTenantPage re-seeds the form from it after a save.
    const res = await axiosInstance.put<Tenant>(TENANT_ENDPOINTS.TENANT_DETAIL(id), data, { skipMultilangStrip: true } as object);
    console.log('TenantFormApiService.updateItem returned:', res.data);
    return res.data;
  },
  getItem: async (id: number | string): Promise<Tenant> => {
    // Raw: the tenant form edits multilang values (login text, legal pages) —
    // the shared interceptor would flatten them to English and the next save
    // would wipe the other languages.
    const res = await axiosInstance.get<Tenant>(TENANT_ENDPOINTS.TENANT_DETAIL(id), { skipMultilangStrip: true } as object);
    console.log('TenantFormApiService.getItem returned:', res.data);
    return res.data;
  },
};

export interface TenantDeleteImpact {
  id: number;
  name: string;
  is_default: boolean;
  home_users: number;
  secondary_members: number;
  courses: number;
  categories: number;
  programs: number;
  cohorts: number;
}

export const TenantApi = {
  /** What deleting the tenant removes and who it affects (Delete tenant dialog). */
  getDeleteImpact: async (tenantId: number): Promise<TenantDeleteImpact> =>
    (await axiosInstance.get(TENANT_ENDPOINTS.TENANT_DELETE_IMPACT(tenantId))).data,

  /** Delete a tenant; its home users are moved to another tenant or suspended. */
  deleteTenant: async (tenantId: number, users: 'move' | 'suspend', targetTenantId?: number): Promise<void> => {
    await axiosInstance.delete(TENANT_ENDPOINTS.TENANT_DETAIL(tenantId), {
      params: { users, ...(users === 'move' && targetTenantId ? { target_tenant_id: targetTenantId } : {}) },
    });
  },

  getUsageStats: async (): Promise<TenantUsageItem[]> =>
    (await axiosInstance.get(TENANT_ENDPOINTS.TENANTS_USAGE)).data,

  switchTenant: async (targetTenantId: number | string) =>
    (await axiosInstance.post(TENANT_ENDPOINTS.SWITCH_TENANT(targetTenantId))).data,

  // ?assets=url: inline base64 images (login image, default covers, logos —
  // ~3.9 MB) come back as small, immutably cached asset URLs. This runs on every
  // signed-in page load. The admin editors load tenants elsewhere (raw values),
  // and the backend refuses to save an asset URL over a stored image.
  getCurrentBranding: async (): Promise<TenantBranding> =>
    (await axiosInstance.get(`${TENANT_ENDPOINTS.TENANT_BRANDING_CURRENT}?assets=url`)).data,

  updateBranding: async (tenantId: number | string, data: TenantBrandingUpdate): Promise<TenantBranding> =>
    (await axiosInstance.put(TENANT_ENDPOINTS.TENANT_BRANDING_DETAIL(tenantId), data)).data,

  /** Set the Explore page hero cover by uploaded file id (site/business admin). Pass null to clear. */
  setExploreCover: async (fileId: number | null): Promise<Record<string, unknown>> =>
    (await axiosInstance.put(TENANT_ENDPOINTS.TENANT_EXPLORE_COVER, { explore_cover_image_id: fileId })).data,

  /** Save the Explore (catalogue) page settings: items per page, filters and card parts shown (course-content managers). */
  setExploreSettings: async (settings: Required<ExploreSettings>): Promise<ExploreSettings> =>
    (await axiosInstance.put(TENANT_ENDPOINTS.TENANT_EXPLORE_SETTINGS, settings)).data,

  /** Set the Communities page hero cover by uploaded file id (community admin). Pass null to clear. */
  setCommunitiesCover: async (fileId: number | null): Promise<Record<string, unknown>> =>
    (await axiosInstance.put(TENANT_ENDPOINTS.TENANT_COMMUNITIES_COVER, { communities_cover_image_id: fileId })).data,

  /** Set one tenant as the platform default (clears default on all others). */
  setDefault: async (tenantId: number | string): Promise<Tenant> =>
    (await axiosInstance.patch<Tenant>(TENANT_ENDPOINTS.TENANT_SET_DEFAULT(tenantId))).data,

  /** Remove the default flag from a tenant. */
  unsetDefault: async (tenantId: number | string): Promise<Tenant> =>
    (await axiosInstance.patch<Tenant>(TENANT_ENDPOINTS.TENANT_UNSET_DEFAULT(tenantId))).data,

  /** Move a list of users (by ID) to a tenant. */
  assignUsers: async (tenantId: number | string, userIds: number[]): Promise<{ moved: number }> =>
    (await axiosInstance.post(TENANT_ENDPOINTS.TENANT_ASSIGN_USERS(tenantId), { user_ids: userIds })).data,

  /** Set a single user's primary tenant (POST /tenants/{tenantId}/members/{userId}). */
  assignUserToTenant: async (tenantId: number | string, userId: number | string): Promise<{ success: boolean }> =>
    (await axiosInstance.post(TENANT_ENDPOINTS.TENANT_MEMBER_DETAIL(tenantId, userId))).data,

  /** Remove a single user from a tenant (DELETE /tenants/{tenantId}/members/{userId}). */
  removeUserFromTenant: async (tenantId: number | string, userId: number | string): Promise<{ success: boolean }> =>
    (await axiosInstance.delete(TENANT_ENDPOINTS.TENANT_MEMBER_DETAIL(tenantId, userId))).data,
};